import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/contexts/AuthContext';
import { getBrandAsset } from '@/themes';
import { Button } from '@/components/ui';
import { hasGoal, RaceGoal, useRaceGoal, type RaceChoice } from './RaceGoal';

/**
 * The public calculator: one sentence to pick the race in and work the goal out, then carry both into sign-up. It keeps nothing, so a reload
 * starts fresh, and the race travels in history state, never the URL, so a shared link carries no one's numbers.
 */
export function WinNumberPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [choice, setChoice] = useState<RaceChoice | null>(null);
  const { ready } = useRaceGoal(choice);
  const raceSet = hasGoal(choice);

  const carry = (raceChoice: RaceChoice) => void navigate({ to: '/get-started', state: { raceChoice } });
  const change = (next: RaceChoice) => (next.kind === 'NoDistrictYet' ? carry(next) : setChoice(next));

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-8">
      <Link to="/" className="mb-8 rounded-xl bg-logo-surface px-5 py-3">
        <img src={getBrandAsset('logo.svg')} alt="Logo" className="h-10" />
      </Link>
      <div className="w-full max-w-3xl">
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <h1 className="mb-1 text-xl font-semibold text-foreground">How many votes do you need to win?</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            {raceSet
              ? `Change any number to see what it does. Nothing is saved until you ${isAuthenticated ? 'start the campaign' : 'sign up'}.`
              : `Pick your race and we’ll work it out.${isAuthenticated ? '' : ' No account needed.'}`}
          </p>
          <RaceGoal choice={choice} onChange={change} />
          {raceSet && (
            <Button type="button" className="mt-6 w-full" disabled={!ready} onClick={() => carry(choice)}>
              {isAuthenticated ? 'Start a campaign for this race' : 'Continue'}
            </Button>
          )}
        </div>
        {!isAuthenticated && (
          <div className="mt-4 space-y-2 text-center text-sm text-muted-foreground">
            {/* Once a race is set, Continue already carries it into sign-up. */}
            {!raceSet && (
              <p>
                <Link to="/get-started" state={choice ? { raceChoice: choice } : undefined} className="font-medium text-primary hover:underline">
                  Skip to create account
                </Link>
              </p>
            )}
            <p>
              Already have an account?{' '}
              <Link to="/login" search={{ redirect: '/win-number' }} className="font-medium text-primary hover:underline">
                Log in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
